import React, { Children, Ref } from 'react';
import { Box, BoxProps, Flex, Grid, GridItem, Stack, StackProps, Center } from '@chakra-ui/react';

type Props = BoxProps &
  Omit<StackProps, 'wrap'> & {
    variant?: 'center' | 'grid' | 'flex' | 'container' | 'box' | 'device-frame' | 'stack';
    wrap?: boolean;
    ref?: Ref<HTMLDivElement>;
    passPropsDownstream?: boolean;
    /** Legacy alias for `gap`. */
    spacing?: StackProps['gap'];
    /** Legacy alias for `separator`. */
    divider?: React.ReactElement;
    separator?: React.ReactElement;
  };

export const Layout = React.forwardRef<HTMLDivElement, Props>(
  (
    { variant = 'box', wrap, children, spacing, gap, divider, separator, ...rest }: Props,
    ref: React.ForwardedRef<HTMLDivElement>
  ) => {
    /**
     * DOM
     */
    if (variant === 'center') {
      return (
        <Center data-object-type="core-layout-center" ref={ref} gap={gap} {...rest}>
          {children}
        </Center>
      );
    }
    if (variant === 'stack') {
      // The legacy name wins on conflict. Stack reads only `gap`/`separator`, and always
      // suppresses the flex `gap` in favour of the separator's own margins once one is present.
      const resolvedGap = spacing ?? gap;
      const resolvedSeparator = divider ?? separator;
      // A raw `gap` beside `divider` spaces items and wrapped rows via rowGap/columnGap, which Stack keeps.
      const legacyDividerGap =
        spacing === undefined && gap !== undefined && divider !== undefined ? { rowGap: gap, columnGap: gap } : {};
      return (
        <Stack
          data-object-type="core-layout-stack"
          {...(wrap && { direction: 'row', flexWrap: 'wrap' })}
          ref={ref}
          gap={resolvedGap}
          separator={resolvedSeparator}
          {...legacyDividerGap}
          {...rest}
        >
          {rest?.passPropsDownstream
            ? Children.map(children, child => {
                if (React.isValidElement(child)) {
                  return React.cloneElement(child, { ...rest });
                }
                return child;
              })
            : children}
        </Stack>
      );
    }
    if (variant === 'flex') {
      return (
        <Flex data-object-type="core-layout-flex" ref={ref} gap={gap} {...rest}>
          {children}
        </Flex>
      );
    }
    if (variant === 'grid') {
      return (
        <Grid ref={ref} data-object-type="core-layout-grid" gap={gap} {...rest}>
          {Children.map(children, child => {
            if (React.isValidElement(child)) {
              return (
                <GridItem key={child.key} display={child.props.display} colSpan={child?.props?.colSpan || 1}>
                  {child}
                </GridItem>
              );
            }
            return child;
          })}
        </Grid>
      );
    }
    if (variant === 'container') {
      return (
        <Box ref={ref} data-object-type="core-layout-container" gap={gap} {...rest}>
          {children}
        </Box>
      );
    }
    return (
      <Box ref={ref} data-object-type="core-layout-box" gap={gap} {...rest}>
        {children}
      </Box>
    );
  }
);
Layout.displayName = 'Layout';
